import { TFunction } from 'i18next';
import { AdminStatistics } from '../types/admin';
import {
  AdminActionItem,
  AdminAttentionItem,
  AdminDashboardViewModel,
  AdminSnapshotCell,
  DashboardUrgentNotice,
} from '../types/dashboard';
import { User } from '../types/auth';

function formatRevenue(amount: number, language: string): string {
  return new Intl.NumberFormat(language.startsWith('el') ? 'el-GR' : 'en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

function buildAttentionItems(stats: AdminStatistics, t: TFunction): AdminAttentionItem[] {
  return [
    {
      id: 'change-requests',
      kind: 'changeRequest',
      title: t('admin.home.attention.changeRequests'),
      count: stats.pendingRequests ?? 0,
      subtitle:
        (stats.pendingFieldChanges ?? 0) > 0
          ? t('admin.home.attention.changeRequestFields', {
              count: stats.pendingFieldChanges,
              defaultValue: '{{count}} pending field changes',
            })
          : undefined,
      link: '/admin/change-requests',
      actionLabel: t('admin.home.attention.review'),
    },
    {
      id: 'registration-petitions',
      kind: 'registrationPetition',
      title: t('admin.home.attention.registrationPetitions'),
      count: stats.pendingRegistrationPetitions ?? 0,
      link: '/admin/petitions',
      actionLabel: t('admin.home.attention.review'),
    },
    {
      id: 'member-petitions',
      kind: 'memberPetition',
      title: t('admin.home.attention.memberPetitions'),
      count: stats.pendingMemberPetitions ?? 0,
      link: '/admin/petitions?tab=member',
      actionLabel: t('admin.home.attention.review'),
    },
    {
      id: 'pending-payments',
      kind: 'payment',
      title: t('admin.home.attention.pendingPayments'),
      count: stats.pendingPayments ?? 0,
      link: `/admin/payments?status=Pending&year=${new Date().getFullYear()}`,
      actionLabel: t('admin.home.attention.manage'),
    },
  ];
}

function buildUrgentNotices(items: AdminAttentionItem[], t: TFunction): DashboardUrgentNotice[] {
  return items
    .filter(item => item.count > 0)
    .slice(0, 3)
    .map(item => ({
      type: 'warning' as const,
      title: item.title,
      message: t('admin.home.attention.pendingCount', { count: item.count }),
      actionLabel: item.actionLabel,
      actionLink: item.link,
    }));
}

function buildSnapshot(stats: AdminStatistics, language: string, t: TFunction): AdminSnapshotCell[] {
  return [
    {
      id: 'members',
      label: t('admin.totalMembers'),
      value: String(stats.totalMembers ?? 0),
      link: '/admin/members',
    },
    {
      id: 'institutions',
      label: t('admin.totalInstitutions'),
      value: String(stats.totalInstitutions ?? 0),
      link: '/admin/institutions',
    },
    {
      id: 'users',
      label: t('admin.totalUsers'),
      value: String(stats.totalUsers ?? 0),
    },
    {
      id: 'revenue',
      label: t('admin.payments.totalRevenue'),
      value: formatRevenue(stats.totalRevenueYtd ?? 0, language),
      link: '/admin/payments',
    },
  ];
}

function buildPrimaryActions(t: TFunction): AdminActionItem[] {
  return [
    {
      id: 'members',
      label: t('admin.manageMembers'),
      description: t('admin.home.actions.membersDesc'),
      link: '/admin/members',
    },
    {
      id: 'institutions',
      label: t('admin.manageInstitutions'),
      description: t('admin.home.actions.institutionsDesc'),
      link: '/admin/institutions',
    },
    {
      id: 'payments',
      label: t('admin.payments.title'),
      description: t('admin.home.actions.paymentsDesc'),
      link: '/admin/payments',
    },
    {
      id: 'vignettes',
      label: t('nav.vignettes'),
      description: t('admin.home.actions.vignettesDesc'),
      link: '/admin/vignettes',
    },
    {
      id: 'email',
      label: t('nav.emailCampaign'),
      description: t('admin.home.actions.emailDesc'),
      link: '/admin/email',
    },
  ];
}

function buildSecondaryActions(t: TFunction): AdminActionItem[] {
  return [
    {
      id: 'import',
      label: t('nav.excelImport'),
      description: t('admin.home.actions.importDesc'),
      link: '/admin/members/import',
    },
    {
      id: 'email-history',
      label: t('nav.emailHistory'),
      description: t('admin.home.actions.emailHistoryDesc'),
      link: '/admin/email/history',
    },
  ];
}

export function buildAdminDashboardViewModel(
  stats: AdminStatistics,
  user: User,
  language: string,
  t: TFunction
): AdminDashboardViewModel {
  const attentionItems = buildAttentionItems(stats, t);
  const totalPendingWork = attentionItems.reduce((sum, item) => sum + item.count, 0);

  const displayName =
    [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
    user.email ||
    t('dashboard.roles.administrator');

  return {
    greeting: t('admin.home.greeting', { name: displayName }),
    description: t('admin.home.description'),
    attentionItems,
    totalPendingWork,
    urgentNotices: buildUrgentNotices(attentionItems, t),
    snapshot: buildSnapshot(stats, language, t),
    primaryActions: buildPrimaryActions(t),
    secondaryActions: buildSecondaryActions(t),
  };
}
